import { ActionModel } from '@klant/conversational-connector/klant-conversational-connector.model';
import { ChatMessageModel, ChatMessageViewModel } from '@klant/conversational-connector/models/ChatMessage.model';
import { TypeModel } from '@klant/conversational-connector/models/Type.model';

import {
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    output,
    signal,
    Signal,
    viewChild,
    viewChildren
} from '@angular/core';

import { ApWindowService } from '@ap/cdk';
import { ApContentService } from '@ap/content';

import { ChatUIStore } from '../../signal-stores/chat-ui-store/chat-ui.store';
import { ConversationalChatStore } from '../../signal-stores/conversational-chat-store/conversational-chat.store';
import { WebsocketService } from '../conversational-connector/services/wsservice/websocket.service';

import { ConversationalChatInputComponent } from './conversational-chat-input/conversational-chat-input.component';
import { ConversationalChatMessageComponent } from './conversational-chat-message/conversational-chat-message.component';
import content from './klant-conversational.content.json';

@Component({
    selector: 'klant-conversational',
    templateUrl: './klant-conversational.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KlantConversationalComponent {
    private readonly apContentService = inject(ApContentService);
    private readonly apWindowService = inject(ApWindowService);
    private readonly conversationalChatStore = inject(ConversationalChatStore);
    private readonly chatUIStore = inject(ChatUIStore);
    private readonly webSocketService = inject(WebsocketService);

    private readonly chatInputContainer = viewChild.required(ConversationalChatInputComponent, { read: ElementRef });
    private readonly chatMessageComponents = viewChildren(ConversationalChatMessageComponent, { read: ElementRef });
    private readonly latestChatMessageComponent = computed(() => {
        const chatMessageComponents = this.chatMessageComponents();
        return chatMessageComponents[chatMessageComponents.length - 1];
    });

    public readonly messageLoading = this.webSocketService.messageLoading;

    public readonly sendAction = output<ActionModel>();
    public readonly sendMessage = output<string>();

    public readonly groupedChatMessages: Signal<ChatMessageViewModel[]> = computed(() => {
        const chatMessages = this.conversationalChatStore.chatMessages();
        return chatMessages.map((message, index) => {
            return {
                ...message,
                firstOfDate: this.firstOfDate(message, chatMessages),
                messageDate: this.getChatMessageDate(message),
                showAvatar: this.showAvatar(message, index, chatMessages)
            };
        });
    });
    private readonly previousChatMessagesLength = signal(0);

    public readonly showMessageLoaderAvatar = computed(() => {
        const chatMessages = this.groupedChatMessages();
        const previousMessage = chatMessages[chatMessages.length - 1];
        return previousMessage.fromClient || (previousMessage.type === TypeModel.PARTICIPANT_MESSAGE && !!previousMessage.introductionText);
    });

    public readonly botOrLivechatAvatar = computed(() => {
        if (this.isChattingWithAgent()) {
            return 'avatar-agent';
        }

        return 'avatar-bot';
    });

    private readonly isChattingWithAgent = computed(() => {
        const botOrAgentMessages: ChatMessageModel[] = this.groupedChatMessages().filter((message) => !message.fromClient);

        const lastMessageFromBotOrAgent = botOrAgentMessages[botOrAgentMessages.length - 1];

        return (
            (lastMessageFromBotOrAgent.type === TypeModel.PARTICIPANT_MESSAGE ||
                lastMessageFromBotOrAgent.type === TypeModel.PLAIN_TEXT_MESSAGE) &&
            lastMessageFromBotOrAgent.serviceName === 'livechat'
        );
    });

    public constructor() {
        this.apContentService.addContentItems(content);

        afterRenderEffect({
            read: () => {
                this.onScrollActionDispatched();
            }
        });

        afterRenderEffect({
            read: () => {
                this.setupAutoScrollBehaviour();
            }
        });
    }

    private firstOfDate(chatMessage: ChatMessageModel, allChatMessages: ChatMessageModel[]): boolean {
        const filteredChatMessages = allChatMessages.filter((message) => {
            const prev = this.getChatMessageDate(chatMessage);
            const current = this.getChatMessageDate(message);

            return (
                prev!.getFullYear() === current!.getFullYear() &&
                prev!.getMonth() === current!.getMonth() &&
                prev!.getDate() === current!.getDate()
            );
        });

        return filteredChatMessages.indexOf(chatMessage) === 0;
    }

    private showAvatar(currentMessage: ChatMessageModel, index: number, allChatMessages: ChatMessageModel[]): boolean {
        if (currentMessage.fromClient) {
            return false;
        }

        if (index === 0 || this.firstOfDate(currentMessage, allChatMessages)) {
            return true;
        }

        const previousMessage = allChatMessages[index - 1];

        return previousMessage?.fromClient || previousMessage?.type === TypeModel.PARTICIPANT_MESSAGE;
    }

    private getChatMessageDate(chatMessage: ChatMessageModel): Date | undefined {
        return !!chatMessage.occurredAt ? new Date(chatMessage.occurredAt / 1000) : undefined;
    }

    public onSendAction(action: ActionModel): void {
        this.sendAction.emit(action);
        this.setFocusToInputAfterDisappearingButton();
    }

    public onSendMessage(message: string): void {
        this.sendMessage.emit(message);
    }

    private setFocusToInputAfterDisappearingButton(): void {
        requestAnimationFrame(() => {
            this.chatInputContainer().nativeElement.querySelector('textarea')?.focus();
        });
    }

    private setupAutoScrollBehaviour(): void {
        const previousLength = this.previousChatMessagesLength();
        const currentLength = this.groupedChatMessages().length;

        if (previousLength !== currentLength || this.messageLoading() === true) {
            this.previousChatMessagesLength.set(currentLength);

            const prefersReducedMotion = this.apWindowService.getWindow().matchMedia('(prefers-reduced-motion: reduce)').matches;

            const isInitialScroll = this.chatUIStore.initialScroll() === true;
            this.chatUIStore.dispatchScrollAction({ behaviour: prefersReducedMotion || isInitialScroll ? 'auto' : 'smooth' });
        }
    }

    private onScrollActionDispatched(): void {
        const { behaviour } = this.chatUIStore.scrollOptions();

        const element = this.latestChatMessageComponent()?.nativeElement;
        if (element) {
            requestAnimationFrame(() => {
                element.scrollIntoView({ behavior: behaviour });
            });
        }
    }
}
